import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Welcome back!");
      navigate(data.role === "ADMIN" ? "/admin" : from, { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(229,9,20,0.12),transparent_50%)]" />
      <div className="relative w-full max-w-md glass rounded-2xl p-8 animate-fade-up">
        <div className="flex items-center gap-2 mb-6">
          <img src="/logo-mark.png" alt="Tazama Africa logo" className="w-10 h-10 rounded-xl object-cover" />
          <span className="font-display font-extrabold text-xl">Tazama <span className="text-[#E50914]">Africa</span></span>
        </div>
        <h1 className="font-display font-black text-3xl mb-1">Welcome back</h1>
        <p className="text-white/50 text-sm mb-6">Sign in to continue streaming.</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Email</label>
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none transition-colors" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Password</label>
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none transition-colors" />
          </div>
          <button data-testid="login-submit" disabled={loading}
            className="w-full h-11 rounded-lg bg-[#E50914] hover:bg-[#FF2A3B] font-semibold transition-colors disabled:opacity-60">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-white/40">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <button data-testid="login-google" onClick={googleLogin}
          className="w-full h-11 rounded-lg bg-white text-black font-semibold flex items-center justify-center gap-2 hover:bg-white/90 transition-colors">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
          Continue with Google
        </button>

        <p className="text-center text-sm text-white/50 mt-6">
          New to Tazama Africa? <Link to="/register" className="text-[#E50914] font-medium hover:underline">Create account</Link>
        </p>
      </div>
    </div>
  );
}
